import type { OrgUnitDto, OrgUnitTreeNode } from '@ukinfo/shared';
import { useQuery } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api } from './api';
import { useAuth } from './auth';

const STORAGE_KEY = 'ukinfo.scope';

export interface ScopeContextValue {
  units: OrgUnitDto[];
  tree: OrgUnitTreeNode[];
  isLoading: boolean;
  /** Aktuell gewaehlter Bereich (null = Gesamtverein). */
  unitId: string | null;
  unit: OrgUnitDto | null;
  setUnitId: (id: string | null) => void;
  includeChildren: boolean;
  setIncludeChildren: (value: boolean) => void;
  /** Beschriftung fuer die Anzeige, z. B. "Fussball > Nachwuchs > U16". */
  breadcrumb: string;
  unitName: (id: string) => string;
  /** Der gewaehlte Bereich inklusive Unterbereiche. */
  scopeIds: string[];
}

const ScopeContext = createContext<ScopeContextValue | null>(null);

function buildTree(units: OrgUnitDto[]): OrgUnitTreeNode[] {
  const nodes = new Map<string, OrgUnitTreeNode>();
  for (const unit of units) nodes.set(unit.id, { ...unit, children: [] });

  const roots: OrgUnitTreeNode[] = [];
  for (const unit of units) {
    const node = nodes.get(unit.id);
    if (!node) continue;
    const parent = unit.parentId ? nodes.get(unit.parentId) : undefined;
    if (parent) parent.children.push(node);
    else roots.push(node);
  }

  const sortNodes = (list: OrgUnitTreeNode[]): void => {
    list.sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name, 'de'));
    for (const child of list) sortNodes(child.children);
  };
  sortNodes(roots);
  return roots;
}

export function ScopeProvider({ children }: { children: ReactNode }): JSX.Element {
  const { user } = useAuth();
  const [unitId, setUnitIdState] = useState<string | null>(
    () => window.localStorage.getItem(STORAGE_KEY) || null,
  );
  const [includeChildren, setIncludeChildren] = useState(true);

  const { data, isLoading } = useQuery({
    queryKey: ['organisation', 'units'],
    queryFn: () => api.get<OrgUnitDto[]>('/api/organisation/units'),
    enabled: !!user,
    staleTime: 60_000,
  });

  const units = useMemo(() => data ?? [], [data]);

  // Ein gespeicherter Bereich, auf den kein Zugriff mehr besteht, wird verworfen.
  useEffect(() => {
    if (!unitId || units.length === 0) return;
    if (!units.some((u) => u.id === unitId)) {
      setUnitIdState(null);
      window.localStorage.removeItem(STORAGE_KEY);
    }
  }, [unitId, units]);

  const setUnitId = useCallback((id: string | null) => {
    setUnitIdState(id);
    if (id) window.localStorage.setItem(STORAGE_KEY, id);
    else window.localStorage.removeItem(STORAGE_KEY);
  }, []);

  const value = useMemo<ScopeContextValue>(() => {
    const byId = new Map(units.map((u) => [u.id, u]));
    const unit = unitId ? (byId.get(unitId) ?? null) : null;

    const unitName = (id: string): string => byId.get(id)?.name ?? 'Unbekannter Bereich';

    const breadcrumb = unit
      ? unit.path.map((id) => byId.get(id)?.name ?? '').filter(Boolean).join(' › ')
      : 'Gesamtverein';

    const scopeIds = unit
      ? includeChildren
        ? units.filter((u) => u.path.includes(unit.id)).map((u) => u.id)
        : [unit.id]
      : units.map((u) => u.id);

    return {
      units,
      tree: buildTree(units),
      isLoading,
      unitId,
      unit,
      setUnitId,
      includeChildren,
      setIncludeChildren,
      breadcrumb,
      unitName,
      scopeIds,
    };
  }, [units, unitId, includeChildren, isLoading, setUnitId]);

  return <ScopeContext.Provider value={value}>{children}</ScopeContext.Provider>;
}

export function useScope(): ScopeContextValue {
  const context = useContext(ScopeContext);
  if (!context) throw new Error('useScope muss innerhalb von ScopeProvider verwendet werden');
  return context;
}

/** Query-Parameter fuer bereichsabhaengige API-Aufrufe. */
export function scopeQuery(scope: ScopeContextValue): {
  organisationUnitId?: string;
  includeChildren: boolean;
} {
  return {
    ...(scope.unitId ? { organisationUnitId: scope.unitId } : {}),
    includeChildren: scope.includeChildren,
  };
}
